import { Box, Grid, TextField, Skeleton } from '@mui/material'
import { useState, useEffect, useRef } from 'react';
import LinearProgress from '@mui/material/LinearProgress';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';

export default function BarcodeField(props: any) {
    const { barcodeValue, setBarcodeValue, machineActive, settings, showMachineSettings, settingsLoaded, scanButtonPressed, setScanButtonPressed, machineScan, drawerOpen, mobileNavbarOpen, scanOnEnter } = props
    const [placeHolderText, setPlaceHolderText] = useState<string>('')
    const [autoFocusBarcode, setAutoFocusBarcode] = useState<boolean>(true);
    const { t } = useTranslation();

    // Ref for the TextField
    const barcodeInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (_.get(settings, 'Scanner.AutoGenerateBarcode.Enabled')) {
            settings.Scanner.AutoGenerateBarcode.Enabled.value === true ? setPlaceHolderText('Barcode Generation On') : setPlaceHolderText('')
        } else {
            setPlaceHolderText('')
        }
        if (_.get(settings, 'Scanner.AlwaysSelectedField')) {
            if (settings.Scanner.AlwaysSelectedField.value === 'Barcode') {
                setAutoFocusBarcode(true)
                if (autoFocusBarcode && barcodeInputRef.current) {
                    barcodeInputRef.current.focus();
                }
            } else {
                setAutoFocusBarcode(false)
            }
        }
    }, [settings, showMachineSettings, settingsLoaded, autoFocusBarcode])

    useEffect(() => {
        if (autoFocusBarcode) {
            // Only focus if no other input element has focus
            const activeElement = document.activeElement;
            const isInputFocused = activeElement &&
                (activeElement.tagName === 'INPUT' || activeElement.tagName === 'TEXTAREA') &&
                activeElement !== barcodeInputRef.current;

            if (!isInputFocused) {
                setTimeout(() => {
                    if (barcodeInputRef.current) {
                        barcodeInputRef.current.focus();
                    }
                }, 100);
            }
            
            // Add event listener for checkbox selection
            const handleCheckboxChange = (event: MouseEvent) => {
                const target = event.target as HTMLElement;
                // Check if the clicked element is a checkbox
                if (target.tagName === 'INPUT' && (target as HTMLInputElement).type === 'checkbox') {
                    // Focus the barcode field after a short delay to allow other logic to complete
                    setTimeout(() => {
                        if (barcodeInputRef.current) {
                            barcodeInputRef.current.focus();
                        }
                    }, 100);
                }
            };
            
            // Add event listener for checkbox changes
            document.addEventListener('click', handleCheckboxChange);
            
            // Clean up event listener when component unmounts
            return () => {
                document.removeEventListener('click', handleCheckboxChange);
            };
        }
    }, [machineScan, autoFocusBarcode, drawerOpen, mobileNavbarOpen]);

    const styles = {
        barcodeComponent: {
            backgroundColor: 'var(--spectre-surface)',
            mt: 1
        },
        barcodeTextArea: {
            backgroundColor: 'var(--spectre-input)',
            '& .MuiInputBase-input': {
                color: 'var(--spectre-text-primary)',
            },
            '& .MuiInputLabel-root.Mui-focused': {
                color: 'var(--spectre-text-primary)',
            },
            '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: '#F7914B',
            },
        }
    }

    const onKeyDownHandler = function (event: any) {
        if (scanOnEnter && !machineActive && event.keyCode === 13) {
            setScanButtonPressed(true);
        }
    }

    return (
        <Box>
            <Grid
                container
                direction="row"
                justifyContent="space-evenly"
                alignItems="center"
            >
                {
                    settingsLoaded
                        ?
                        <Grid item md={11} xs={11} sx={styles.barcodeComponent}>
                            {machineActive && settings.Scanner.AutoGenerateBarcode.Enabled.value && barcodeValue.length === 0 && scanButtonPressed ? <LinearProgress sx={{
                                backgroundColor: '#262D6D',
                                '& .MuiLinearProgress-bar': {
                                    backgroundColor: "#F48430",
                                }
                            }}></LinearProgress> : ''}
                            <TextField
                                id="barcode-field"
                                variant="outlined"
                                label={t('control.inputs.barcode')}
                                onKeyDown={onKeyDownHandler}
                                fullWidth
                                sx={styles.barcodeTextArea}
                                placeholder={placeHolderText}
                                value={barcodeValue}
                                onChange={(element) => { setBarcodeValue(element.target.value) }}
                                inputRef={barcodeInputRef}
                            />
                            {machineActive && settings.Scanner.AutoGenerateBarcode.Enabled.value && barcodeValue.length === 0 && scanButtonPressed ? <LinearProgress sx={{
                                backgroundColor: '#262D6D',
                                '& .MuiLinearProgress-bar': {
                                    backgroundColor: "#F48430"
                                }
                            }}></LinearProgress> : ''}
                        </Grid>
                        :
                        <Grid item xs={11}>
                            <Skeleton animation="wave" variant="rounded" width={"100%"} height={60} sx={{ bgcolor: 'var(--spectre-input)' }} />
                        </Grid>
                }
            </Grid>
        </Box>
    )
}